import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import red from "@material-ui/core/colors/red";
import { useEffect, useState } from "react";

import { participantPortalUrl } from "../../../common/config";
import { useSearchParams } from "../../../shared/hooks/useSearchParams";
import { validatePassword } from "../../../shared/validatePassword";
import {
  resetPasswordService,
  validateResetPasswordService,
} from "../../services";

const initialFormState = {
  newPassword: "",
  confirmPassword: "",
};

export const ResetPasswordPage = () => {
  const query = useSearchParams();
  const [isFething, setIsFetching] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [username, setUsername] = useState("");
  const [isIdValid, setIsIdValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [formValues, setFormValues] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState(initialFormState);

  const queryId = query.get("id");

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await validateResetPasswordService({ id: queryId });

        if (response.status !== 200) {
          setIsIdValid(false);
          return;
        }

        setUsername(response.data.data);
      } catch (e) {
        setIsIdValid(false);
      } finally {
        setIsFetching(false);
      }
    };

    fetch();
  }, [queryId]);

  const getIsDisabled = () => {
    const { newPassword, confirmPassword } = formErrors;

    return isUpdating || !!(newPassword.length || confirmPassword.length);
  };

  const handleBlur = (e) => {
    const { value, name } = e.target;

    if (!value) {
      setFormErrors({ ...formErrors, [name]: "This field is required" });
      return;
    }

    const validationResult = validatePassword(value);
    if (!validationResult.isValid) {
      setFormErrors({
        ...formErrors,
        [name]:
          "Must include 8 characters, 1 number, 1 special character and 1 alphabetic value at least",
      });
      return;
    }

    if (name === "confirmPassword" && value !== formValues.newPassword) {
      setFormErrors({
        ...formErrors,
        [name]: "The password doesn't match",
      });
      return;
    }

    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const handleChange = ({ target: { name, value } }) =>
    setFormValues({ ...formValues, [name]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const response = await resetPasswordService({
      id: queryId,
      password: formValues.newPassword,
      confirmPassword: formValues.confirmPassword,
    });

    setIsUpdating(false);

    if (response.status !== 200) {
      const message = response.data.errors.length
        ? response.data.errors.toString()
        : response.data.warnings.toString();
      setErrorMessage(message);
      return;
    }

    window.location.href = `${participantPortalUrl}/`;
  };

  const handleCancel = () => {
    setFormErrors(initialFormState);
    setFormValues(initialFormState);
    setErrorMessage("");
  };

  if (isFething) {
    return <>Loading...</>;
  }

  return (
    <Container component="main" maxWidth="xs">
      <Grid container spacing={4} direction="column">
        <Box mt={4} />
        <Grid item>
          <Typography component="h1" variant="h5" align="center">
            Reset Password
          </Typography>
        </Grid>

        <Grid item>
          <Typography align="center">Username {username}</Typography>
        </Grid>

        {!queryId || !isIdValid || errorMessage ? (
          <Grid item>
            <Typography style={{ color: red[500], textAlign: "center" }}>
              {errorMessage ||
                "The reset password link has expired, please go to reset password page again"}
            </Typography>
          </Grid>
        ) : null}

        <Grid item>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4} direction="column">
              <Grid item>
                <TextField
                  type="password"
                  name="newPassword"
                  label="New password"
                  id="newPassword"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={formValues.newPassword}
                  error={!!formErrors.newPassword.length}
                  helperText={formErrors.newPassword}
                />
              </Grid>

              <Grid item>
                <TextField
                  type="password"
                  name="confirmPassword"
                  label="Confirm password"
                  id="confirmPassword"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={formValues.confirmPassword}
                  error={!!formErrors.confirmPassword.length}
                  helperText={formErrors.confirmPassword}
                />
              </Grid>

              <Grid item>
                <Button
                  color="primary"
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={getIsDisabled()}
                >
                  Reset password
                </Button>
              </Grid>

              <Grid item>
                <Button fullWidth onClick={handleCancel}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};
